/*
 * <nl-w2-rail-nav data-rail="ScrollRailId">
 *   [data-nav-prev], [data-nav-next]     external arrow buttons (desktop)
 *   [data-nav-meter] > [data-nav-thumb]   thin scroll meter (thumb width = visible share, position = scroll progress)
 *
 * Drives a foundation <scroll-rail> (nl-app.js) from the outside: paging uses scrollRail.page(), the meter writes the
 * custom properties --thumb and --progress (transform only). The target can be swapped at runtime with
 * `nav.rail = element` (the tabs rail does this on every tab change). Sets data-no-overflow on itself when the
 * track fits. Used by sections/nl-w2-tabs-rail.liquid and sections/nl-w2-reels.liquid.
 */
(() => {
  'use strict';

  if (customElements.get('nl-w2-rail-nav')) return;

  const reduced = () =>
    (window.NL && window.NL.motion && window.NL.motion.reduced) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class W2RailNav extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.prev = this.querySelector('[data-nav-prev]');
      this.next = this.querySelector('[data-nav-next]');
      this.thumb = this.querySelector('[data-nav-thumb]');
      this.onScroll = () => this.queue();
      this.onPrev = () => this.page(-1);
      this.onNext = () => this.page(1);
      if (this.prev) this.prev.addEventListener('click', this.onPrev);
      if (this.next) this.next.addEventListener('click', this.onNext);
      if ('ResizeObserver' in window) this.resizeObserver = new ResizeObserver(() => this.queue());
      const target = this.dataset.rail ? document.getElementById(this.dataset.rail) : null;
      this.rail = target || this.querySelector('scroll-rail');
    }

    disconnectedCallback() {
      this.detach();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.prev) this.prev.removeEventListener('click', this.onPrev);
      if (this.next) this.next.removeEventListener('click', this.onNext);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this._bound = false;
    }

    get rail() {
      return this._rail || null;
    }

    set rail(element) {
      this.detach();
      this._rail = element || null;
      this.track = element ? element.querySelector('[data-rail-track]') : null;
      if (!this.track) {
        this.update();
        return;
      }
      if (element.id) {
        if (this.prev) this.prev.setAttribute('aria-controls', this.track.id || element.id);
        if (this.next) this.next.setAttribute('aria-controls', this.track.id || element.id);
      }
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.resizeObserver) this.resizeObserver.observe(this.track);
      this.update();
    }

    detach() {
      if (this.track) {
        this.track.removeEventListener('scroll', this.onScroll);
        if (this.resizeObserver) this.resizeObserver.unobserve(this.track);
      }
      this.track = null;
    }

    page(direction) {
      const rail = this._rail;
      if (rail && typeof rail.page === 'function') {
        rail.page(direction);
        return;
      }
      if (this.track) {
        this.track.scrollBy({ left: direction * this.track.clientWidth * 0.9, behavior: reduced() ? 'auto' : 'smooth' });
      }
    }

    queue() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const track = this.track;
      if (!track) {
        this.toggleAttribute('data-no-overflow', true);
        return;
      }
      const max = track.scrollWidth - track.clientWidth;
      const x = Math.abs(track.scrollLeft);
      const none = max <= 2;
      this.toggleAttribute('data-no-overflow', none);
      if (this.prev) this.prev.disabled = none || x <= 2;
      if (this.next) this.next.disabled = none || x >= max - 2;
      if (this.thumb) {
        const share = none ? 1 : Math.max(0.08, track.clientWidth / track.scrollWidth);
        const progress = none ? 0 : Math.min(1, x / max);
        this.thumb.style.setProperty('--thumb', share.toFixed(4));
        this.thumb.style.setProperty('--progress', progress.toFixed(4));
      }
    }
  }

  customElements.define('nl-w2-rail-nav', W2RailNav);
})();
